import { type ChangeEventHandler, useMemo, useRef, useState } from 'react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Icon, SlimButton } from 'components'

import * as S from './styles'

type onChangeType = ChangeEventHandler<HTMLTextAreaElement>

export type InputMenuProps = {
  label: string
  placeholder?: string
  value: string
  onChange: onChangeType
  clearAction?: () => void
  searchAction?: () => void
  disabled?: boolean
  hasBorder?: boolean
  fullWidth?: boolean
}

export const InputMenu: React.FC<InputMenuProps> = ({
  label,
  placeholder = 'Buscar',
  value,
  onChange,
  clearAction,
  searchAction,
  disabled,
  hasBorder = true,
  fullWidth = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setTimeout(focusInput, 100)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'multiselect-popover' : undefined

  const focusInput = () => {
    inputRef.current?.focus()
  }

  const labelComplement = useMemo(() => {
    if (!value) return ''
    const batch = value.split('\n').filter((item) => item.trim() !== '')
    if (!batch.length) return ''
    return ` •  ${batch[0]} ${batch.length > 1 ? `(+${batch.length - 1})` : ''}`
  }, [value])

  return (
    <S.Wrapper fullWidth={fullWidth}>
      <S.StyledButton
        aria-describedby={id}
        disabled={disabled}
        disableElevation
        endIcon={<KeyboardArrowDownIcon color="primary" />}
        fullWidth={fullWidth}
        hasBorder={hasBorder}
        id={`input-menu-${label}`}
        onClick={handleClick}
        open={open}
        variant="contained"
      >
        {label}
        {labelComplement}
      </S.StyledButton>
      <S.StyledPopover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        elevation={0}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <S.PopoverContent>
          <S.InputWrapper>
            <S.InputContainer onClick={focusInput}>
              <textarea
                id="menu-area-input"
                onChange={onChange}
                placeholder={placeholder}
                ref={inputRef}
                value={value}
              />
            </S.InputContainer>
          </S.InputWrapper>

          <S.ActionsWrapper>
            {clearAction && (
              <SlimButton
                disabled={!value}
                icon={<Icon icon="clear-all" size="20" />}
                id="input-menu-clear-button"
                onClick={clearAction}
                type="button"
                variant="secondary"
              />
            )}
            <SlimButton
              icon={<Icon icon="save" size="20" />}
              id="input-menu-save-button"
              onClick={handleClose}
              type="button"
              variant="secondary"
            />
            {searchAction && (
              <SlimButton
                content="Buscar"
                id="input-menu-search-button"
                onClick={searchAction}
                type="button"
                variant="primary"
              />
            )}
          </S.ActionsWrapper>
        </S.PopoverContent>
      </S.StyledPopover>
    </S.Wrapper>
  )
}
